import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Gauge, ImageIcon, ExternalLink } from 'lucide-react';
import { api } from '../lib/api';
import { cn } from '../lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';

interface AiUsageShape {
  text: { used: number; limit: number };
  cover: { used: number; limit: number };
  credits?: number;
}

// Dispatch this event from anywhere after an AI action to trigger a refetch
export function triggerAiUsageRefresh() {
  window.dispatchEvent(new Event('ai-usage:refresh'));
}

function ratioOf(used: number, limit: number): number {
  if (limit === -1 || limit === 0) return 0;
  return used / limit;
}

function pillColorClasses(ratio: number): string {
  if (ratio >= 1) return 'text-destructive bg-destructive/10 border-destructive/20';
  if (ratio >= 0.8) return 'text-amber-500 bg-amber-500/10 border-amber-500/20';
  return 'text-muted-foreground bg-muted border-border/70';
}

function barColorClass(ratio: number): string {
  if (ratio >= 1) return 'bg-destructive';
  if (ratio >= 0.8) return 'bg-amber-500';
  return 'bg-primary';
}

function fmtUsed(n: number): string {
  return String(n);
}

function fmtLimit(n: number): string {
  return n === -1 ? '∞' : String(n);
}

/**
 * AI usage indicator.
 * - variant="pill": compact pill (editor topbar).
 * - variant="bar":  slim full-width usage bar (sidebar bottom) — always visible.
 * Both open the same detail popover.
 */
export function AiUsagePill({ variant = 'pill' }: { variant?: 'pill' | 'bar' } = {}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [usage, setUsage] = useState<AiUsageShape | null>(null);

  const refresh = useCallback(async () => {
    try {
      // The backend may return credits even though the TS type omits it
      const data = await api.getAiUsage();
      setUsage(data as AiUsageShape);
    } catch {
      // Silently ignore — never break the editor for a stats fetch
    }
  }, []);

  useEffect(() => {
    refresh();
    // Refresh every 60 s
    const interval = setInterval(refresh, 60_000);
    // Refresh when the tab regains focus
    window.addEventListener('focus', refresh);
    // Refresh on custom event dispatched after AI actions
    window.addEventListener('ai-usage:refresh', refresh);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', refresh);
      window.removeEventListener('ai-usage:refresh', refresh);
    };
  }, [refresh]);

  // Don't render anything until the first fetch resolves
  if (!usage) return null;

  const textRatio = ratioOf(usage.text.used, usage.text.limit);
  const coverRatio = ratioOf(usage.cover.used, usage.cover.limit);
  const unlimited = usage.text.limit === -1;
  const pct = Math.min(100, Math.round(textRatio * 100));

  return (
    <Popover>
      <PopoverTrigger
        className={cn(
          variant === 'bar'
            ? 'group flex w-full items-center gap-2 rounded-xl border border-border bg-muted/30 px-2.5 py-2 text-left hover:bg-muted'
            : cn(
                'inline-flex items-center gap-1 rounded-full border px-2 py-0.5',
                'text-[11px] font-mono font-medium leading-none',
                'hover:opacity-80 active:opacity-70',
                pillColorClasses(textRatio),
              ),
          'transition-colors duration-150 cursor-pointer select-none',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        )}
        title={t('aiUsagePill.title', 'Uso de IA')}
        aria-label={t('aiUsagePill.ariaLabel', 'Uso de IA este mês')}
      >
        {variant === 'bar' ? (
          <>
            <Gauge
              size={13}
              className={cn(
                'shrink-0',
                textRatio >= 1 ? 'text-destructive' : textRatio >= 0.8 ? 'text-amber-500' : 'text-primary',
              )}
            />
            <span className="font-mono text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
              {t('aiUsagePill.aiAbbrev', 'IA')}
            </span>
            {unlimited ? (
              <span className="flex-1 text-right font-mono text-[10px] tabular-nums text-foreground">
                {usage.text.used} / ∞
              </span>
            ) : (
              <>
                <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
                  <div
                    className={cn(
                      'h-full rounded-full transition-[width] duration-500 ease-out',
                      barColorClass(textRatio),
                    )}
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <span className="shrink-0 font-mono text-[10px] tabular-nums text-foreground">
                  {usage.text.used}/{fmtLimit(usage.text.limit)}
                </span>
              </>
            )}
          </>
        ) : (
          <>
            <Sparkles size={11} className="shrink-0" />
            {/* Number hidden on very small screens; icon+color alone signals status */}
            <span className="hidden sm:inline tabular-nums">
              {usage.text.used}/{fmtLimit(usage.text.limit)}
            </span>
          </>
        )}
      </PopoverTrigger>

      <PopoverContent
        side={variant === 'bar' ? 'top' : 'bottom'}
        align={variant === 'bar' ? 'start' : 'end'}
        className="w-72 p-4 space-y-4"
      >
        {/* Header */}
        <div className="flex items-center gap-2 pb-3 border-b border-border">
          <span className="flex h-6 w-6 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Sparkles size={12} />
          </span>
          <h3 className="font-mono text-[11px] font-bold uppercase tracking-widest text-primary">
            {t('aiUsagePill.monthlyUsage', 'Uso de IA — este mês')}
          </h3>
        </div>

        {/* Usage bars */}
        <div className="space-y-4">
          <UsageBarRow
            label={t('aiUsagePill.textActions', 'Ações de texto')}
            used={usage.text.used}
            limit={usage.text.limit}
            ratio={textRatio}
          />
          <UsageBarRow
            label={t('aiUsagePill.coversGenerated', 'Capas geradas')}
            used={usage.cover.used}
            limit={usage.cover.limit}
            ratio={coverRatio}
          />
        </div>

        {/* Cover credits balance (only when returned by the API) */}
        {typeof usage.credits === 'number' && (
          <div className="flex items-center justify-between gap-2 pt-3 border-t border-border">
            <span className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              <ImageIcon size={11} className="shrink-0" />
              {t('aiUsagePill.coverCredits', 'Créditos de capa')}
            </span>
            <span className="font-mono text-[11px] font-semibold text-foreground tabular-nums">
              {usage.credits}
            </span>
          </div>
        )}

        {/* CTA */}
        <button
          type="button"
          onClick={() => navigate('/app/account?tab=subscription')}
          className={cn(
            'w-full flex items-center justify-center gap-1.5',
            'rounded-lg border border-border px-3 py-2',
            'text-[11px] font-mono text-muted-foreground',
            'hover:bg-muted hover:text-foreground transition-colors duration-150',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          )}
        >
          <ExternalLink size={11} />
          {t('aiUsagePill.viewPlansAndUsage', 'Ver planos e uso completo')}
        </button>
      </PopoverContent>
    </Popover>
  );
}

/** Inline progress bar row used in the popover detail. */
function UsageBarRow({
  label,
  used,
  limit,
  ratio,
}: {
  label: string;
  used: number;
  limit: number;
  ratio: number;
}) {
  const unlimited = limit === -1;
  const pct = Math.min(100, Math.round(ratio * 100));

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between gap-2">
        <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
          {label}
        </span>
        <span
          className={cn(
            'font-mono text-[11px] tabular-nums font-medium',
            ratio >= 1
              ? 'text-destructive'
              : ratio >= 0.8
              ? 'text-amber-500'
              : 'text-foreground',
          )}
        >
          {fmtUsed(used)} / {fmtLimit(limit)}
        </span>
      </div>
      {!unlimited && (
        <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
          <div
            className={cn(
              'h-full rounded-full transition-[width] duration-500 ease-out',
              barColorClass(ratio),
            )}
            style={{ width: `${pct}%` }}
          />
        </div>
      )}
    </div>
  );
}
